
/**
 * JourneysDetail/utilities/steps-graph.js
 * desc: set of utility functions to work with graph of journey steps
 * note: used in Journey builder UI to perform operations on steps using the graph data structure
 */
'use strict';
 
import Graph from 'ym-graph';
import { nodesOnAnyPath } from 'ym-graph/reachability';

// global steps graph entity
export const stepsGraph = new Graph();



/**
 * createStepsGraph
 * desc: will create a new graph based on step ids and save it to global variable
 * note: uses `graphology` lib, will create a new graph on each call
 * @param  {array}     steps    steps array
 *
 * @return {undefined}
 */
export const createStepsGraph = (steps) => {
    stepsGraph.clear();

    // adding nodes
    steps.forEach(step => {
        stepsGraph.addNode(step.id);
    });
    // adding edges
    steps.forEach(step => {
        if (step.toSteps && step.toSteps.length > 0) {
            step.toSteps.forEach(childStep => {
                stepsGraph.addEdge(step.id, childStep.stepId);
            });
        }
    });

    return stepsGraph;
};



/**
 * getAvailableSteps
 * desc: will create a simple paths between source and destination steps and then
 *       will do a bfs on a graph and will mark the traversing step `isAvailable` if it is in any of the paths (and is not the destination step itself)
 * note: uses `graphology` lib, will return a Promise
 * @param  {string}     sourceStepId         step id
 * @param  {string}     destinationStepId    step id
 *
 * @return {Promise}
 */
export const getAvailableSteps = (sourceStepId, destinationStepId, steps) => {
    return new Promise((resolve, reject) => {
        if (!sourceStepId || !destinationStepId) {
            return reject(new Error('Please provide source and destination step ids'));
        }
        try {
            const stepsGraph = createStepsGraph(steps);
            const stepsById = steps.reduce((acc, step) => {
                acc[step.id] = step;
                return acc;
            }, {});
            const isDestinationStepInLoop = !!stepsById[destinationStepId]?.metadata?.startLoopStepId;
            let availableLoopsIds = new Set();
            if (isDestinationStepInLoop) {
                availableLoopsIds = _getAllAvailableLoopsIds(stepsById, destinationStepId);
            }

            const pathNodes = nodesOnAnyPath(stepsGraph, sourceStepId, destinationStepId);
            const result = {};

            stepsGraph.forEachNode((node) => {
                let isAvailable = pathNodes.has(node) && node !== destinationStepId;

                // if destination step is in loop then we need to additionally check that the step is in the same loop or in the parent loops
                if (isDestinationStepInLoop && isAvailable) {
                    const stepLoopId = stepsById[node]?.metadata?.startLoopStepId;
                    if (stepLoopId) {
                        // step is in a loop, check if it's in the available loops
                        if (!availableLoopsIds.has(stepLoopId)) {
                            isAvailable = false;
                        }
                    }
                } else if (!isDestinationStepInLoop && isAvailable) {
                    // if destination step is not in a loop, steps inside a loop should not be available
                    if (stepsById[node]?.metadata?.startLoopStepId) {
                        isAvailable = false;
                    }
                }

                result[node] = isAvailable;
            });

            resolve(result);
        } catch (e) {
            reject(e);
        }
    });
};

const _getAllAvailableLoopsIds = (stepsById, destinationStepId) => {
    let currentLoopId = stepsById[destinationStepId]?.metadata?.startLoopStepId;
    const availableLoopsIds = new Set();

    while (currentLoopId) {
        availableLoopsIds.add(currentLoopId);
        // find parent loop step
        currentLoopId = stepsById[currentLoopId]?.metadata?.startOuterLoopStepId ?? null;
    }

    return availableLoopsIds;
};
